import { useState, useCallback, useRef } from 'react';
import { listConversations as getConversationHistory, createConversation } from '../services/conversations.js';
import { useChatApiClient } from '../context/ChatApiClientContext.jsx';

/**
 * API may return HTTP 200 with an envelope `{ statusCode, msgCode, body }` where `statusCode >= 400`.
 * @param {unknown} response
 * @returns {Error|null}
 */
const envelopeErrorFromConversationListResponse = (response) => {
  if (!response || typeof response !== 'object') return null;
  const raw = response.statusCode ?? response.status_code;
  if (raw == null) return null;
  const statusCode = Number(raw);
  if (!Number.isFinite(statusCode) || statusCode < 400) return null;

  const msgList = response.msgCode ?? response.msg_code;
  const first =
    Array.isArray(msgList) && msgList.length > 0 && typeof msgList[0] === 'object'
      ? msgList[0]
      : null;
  const description =
    (first && typeof first.description === 'string' && first.description) || 'Request failed';

  const err = new Error(description);
  err.statusCode = statusCode;
  err.msgCode = Array.isArray(msgList) ? msgList : [];
  err.isConversationListEnvelopeError = true;
  return err;
};

/**
 * Hook that handles conversation list and selected conversation for the current agent.
 * Calls the service getConversationHistory() directly; keeps local state only (no global store).
 * Same pattern as useHomeChatAgent: shared services + local module state.
 */
export const useHomeChatConversations = () => {
  const apiClient = useChatApiClient();
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  /**
   * Fetches conversation history for the given agent and user from the service.
   * Ignores responses for older requests when switching agents quickly.
   * @param {string} agentId - Agent UUID
   * @param {string|null|undefined} userId - User UUID (uses FALLBACK_USER_ID when falsy)
   * @returns {Promise<Array>} Mapped conversations list
   */
  const fetchConversations = useCallback(async (agentId, userId) => {
    if (!agentId) {
      setConversations([]);
      setError(null);
      return [];
    }

    const requestId = ++fetchIdRef.current;
    try {
      setIsLoadingConversations(true);
      setError(null);
      // Don't clear the list up-front: the success path replaces it wholesale
      // (so a previous agent's rows never linger), and an error path keeps the
      // prior list instead of flashing empty / wiping on a transient 4xx.

      const response = await getConversationHistory(apiClient, { userId: userId ?? "", agentId });
      if (requestId !== fetchIdRef.current) return [];

      const envelopeErr = envelopeErrorFromConversationListResponse(response);
      if (envelopeErr) {
        if (requestId === fetchIdRef.current) {
          // The history list is a secondary surface (the conversation thread and
          // live WS updates are independent). A 4xx envelope here (e.g. an empty
          // user id before auth settles) must not spam the console as an error or
          // wipe an already-loaded list — keep prior conversations and record the
          // state quietly for the history panel's own error UI.
          console.warn('Conversation history unavailable:', envelopeErr.message);
          setError(envelopeErr);
        }
        return [];
      }

      const list = response.body || response.conversations || [];

      const mapped = list
        .sort((a, b) => {
          const dateA = new Date(a.lastUpdate || a.startedAt || 0).getTime();
          const dateB = new Date(b.lastUpdate || b.startedAt || 0).getTime();
          return dateB - dateA;
        })
        .map((conv) => ({
          id: conv.conversationUuid,
          uuid: conv.conversationUuid,
          conversationUuid: conv.conversationUuid,
          conversation_uuid: conv.conversationUuid,
          title: conv.title || 'Untitled',
          last_modified: conv.lastUpdate || conv.startedAt,
          startedAt: conv.startedAt,
          agent_id: agentId,
          participants: conv.participants || [],
        }));

      if (requestId !== fetchIdRef.current) return [];
      setConversations(mapped);
      return mapped;
    } catch (err) {
      if (requestId === fetchIdRef.current) {
        console.warn('Conversation history request failed:', err?.message ?? err);
        setError(err);
      }
      return [];
    } finally {
      if (requestId === fetchIdRef.current) {
        setIsLoadingConversations(false);
      }
    }
  }, [apiClient]);

  const selectConversation = useCallback((id) => {
    setSelectedConversationId(id);
    setConversationId(id);
  }, []);

  const clearConversationSelection = useCallback(() => {
    setSelectedConversationId(null);
    setConversationId(null);
  }, []);

  const setLastConversationId = useCallback((id) => {
    setConversationId(id);
  }, []);

  const addConversation = useCallback((conversation) => {
    setConversations((prev) => {
      const existing = prev.findIndex(
        (c) => c.id === conversation.id || c.uuid === conversation.uuid
      );
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], ...conversation };
        return next;
      }
      return [conversation, ...prev];
    });
  }, []);

  /**
   * Creates a new conversation for the given agent.
   * Adds the created conversation to the list and optionally selects it.
   * @param {string} agentId - Agent UUID (assistant UUID)
   * @param {Object} [options] - Optional parameters
   * @param {string} [options.title] - Optional title for the conversation
   * @param {boolean} [options.selectAfterCreate=true] - Whether to select the conversation after creating it
   * @returns {Promise<Object>} The created conversation object
   */
  const createNewConversation = useCallback(async (agentId, options = {}) => {
    if (!agentId) {
      const error = new Error('Agent ID is required to create a conversation');
      setError(error);
      throw error;
    }

    const { title, selectAfterCreate = true } = options;

    try {
      setIsCreatingConversation(true);
      setError(null);

      const response = await createConversation(apiClient, {
        assistantUuid: agentId,
        title: title || 'New Conversation',
      });

      // Extract conversation data from response
      // Response structure may vary: { body: {...} } or direct object
      const responseData = response.body || response.data || response;
      const conversationUuid =
        responseData.conversationUuid ||
        responseData.conversation_uuid ||
        responseData.uuid ||
        responseData.id;

      if (!conversationUuid) {
        throw new Error('Conversation UUID not found in response');
      }

      // Map response to match the conversation structure used in this hook
      const newConversation = {
        id: conversationUuid,
        uuid: conversationUuid,
        conversationUuid: conversationUuid,
        conversation_uuid: conversationUuid,
        title: responseData.title || title || 'Untitled',
        last_modified: responseData.lastUpdate || responseData.last_modified || new Date().toISOString(),
        startedAt: responseData.startedAt || responseData.createdAt || new Date().toISOString(),
        agent_id: agentId,
        participants: responseData.participants || [],
      };

      // Add conversation to the list
      addConversation(newConversation);

      // Select the conversation if requested
      if (selectAfterCreate) {
        selectConversation(conversationUuid);
      }

      return newConversation;
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError(err);
      throw err;
    } finally {
      setIsCreatingConversation(false);
    }
  }, [addConversation, selectConversation, apiClient]);

  return {
    conversations,
    setConversations,
    selectedConversationId,
    setSelectedConversationId,
    conversationId,
    setConversationId,
    setLastConversationId,
    isLoadingConversations,
    setIsLoadingConversations,
    isCreatingConversation,
    error,
    fetchConversations,
    selectConversation,
    clearConversationSelection,
    addConversation,
    createNewConversation,
  };
};

export { useHomeChatConversations as useChatConversations };
